<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
const props = defineProps<{
  name: string
  required: boolean
  label?: string
  validation?: string
  validationMessages?: {
    [key: string]: string
  }
  value?: string
  options: {
    value: string | number
    help?: string
    label: string
    attrs?: {
      [key: string]: string | number | boolean
    }
  }[]
  id?: string
}>()

const validationString = computed(() => {
  const validationString = props.validation ?? ''
  const v = validationString.split('|')
  if (props.required) {
    v.push('required')
  }

  return v.join('|')
})

const valueOrFirstOption = computed(() => props.value ?? props.options[0]?.value)
</script>

<template>
<FormKitLazyProvider config-file="true">

  <FormKit
    :id="id"
    type="radio"
    :name="name"
    :label="label ?? ''"
    :value="valueOrFirstOption"
    :options="options"
    :validation="validationString ?? ''"
    :validation-messages="validationMessages ?? {}"
    :classes="{
      outer: `outer-radio ${$attrs.class ?? ''}`,
      fieldset: 'radio-fieldset',
    }"
  />

</FormKitLazyProvider>
</template>

<style lang="scss" scoped>
.outer-radio {
  &[data-invalid='true'] {
    :deep(.formkit-inner) {
      &:before {
        --form-input-border-color: var(--c-red-90) !important;
        --form-input-label-color: var(--c-red-90) !important;
      }
    }

    :deep(.formkit-label) {
      color: var(--c-red-90) !important;
    }
  }
}

:deep(.radio-fieldset) {
  border: 0;
  padding: 0;

  .formkit-legend {
    margin-bottom: 12px;

    color: var(--c-blue-100);
    font-size: 16px;
    font-weight: 700;
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 20px;
    list-style: none;

    margin: 0;
    padding: 0;

    @include breakpoint(tl) {
      flex-direction: row;
    }

    li {
      min-width: 110px;

      &[data-disabled='true'] {
        opacity: 0.4;

        pointer-events: none;
      }

      .formkit-wrapper {
        display: flex;
        align-items: center;
        gap: 16px;

        &[data-checked='true'] {
          --form-input-background: var(--c-blue-100);
          .formkit-inner {
            &:after {
              opacity: 1;
              visibility: visible;
            }
          }
        }

        &:not([data-checked='true']) {
          .formkit-inner:hover {
            &:before {
              --form-input-border-color: var(--c-blue-100);
              outline-color: var(--form-input-border-color);
              box-shadow: var(--form-input-box-shadow);
            }
          }
        }

        .formkit-inner {
          --input-height: 26px;
          --input-width: 26px;
          --input-border: 1px;

          position: relative;
          line-height: 1;

          height: calc(var(--input-height) - var(--input-border) * 2);
          width: calc(var(--input-width) - var(--input-border) * 2);
          min-width: calc(var(--input-width) - var(--input-border) * 2);

          display: flex;
          justify-content: center;
          align-items: center;

          cursor: pointer;

          &:before,
          &:after {
            position: absolute;
            top: 50%;
            left: 50%;

            transform: translate(-50%, -50%);
          }

          &:before {
            content: '';

            border-radius: 50%;
            border: var(--input-border) solid var(--form-input-border-color);
            outline: 0.5px solid var(--outline-color);
            box-shadow: none;

            height: 100%;
            width: 100%;

            background: var(--form-input-background);

            @include transitions(border, outline, background, box-shadow);
          }

          &:after {
            content: '';

            height: calc(var(--input-height) / 2);
            width: calc(var(--input-width) / 2);

            border-radius: 50%;
            background: var(--c-white);

            opacity: 0;
            visibility: hidden;

            transition: 300ms ease-in-out;
          }

          input {
            opacity: 0;

            height: 100%;
            width: 100%;

            cursor: pointer;
          }
        }

        .formkit-label {
          color: var(--c-gray-100);
          font-size: 18px;
          line-height: 1;
          font-weight: 400;
          opacity: 1;
          letter-spacing: initial;
          text-transform: none;

          position: static;
          transform: translateY(0);
        }
      }

      .formkit-option-help {
        margin-top: 10px;
        color: var(--c-gray-75);
        @include f-13;
      }
    }
  }
}
</style>
